import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Package, Users, Bell, Truck, CheckSquare, Square, Plus, Phone, Bell as BellIcon, CircleDot, Lightbulb, ArrowRight, Database, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICON = { call: Phone, follow_up: Users, reminder: BellIcon, other: CircleDot };
const PRIORITY_DOT = { high: "bg-red-500", medium: "bg-yellow-400", low: "bg-gray-300" };

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [todayTasks, setTodayTasks] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [diagnostic, setDiagnostic] = useState(null);
  const [showDiag, setShowDiag] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [overdueDays, setOverdueDays] = useState(5);
  const [showThresholdEdit, setShowThresholdEdit] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, tasksRes, sugRes, settingsRes] = await Promise.all([
          axios.get(`${API}/admin/stats`, { withCredentials: true }),
          axios.get(`${API}/tasks?date=today_overdue&status=pending`, { withCredentials: true }),
          axios.get(`${API}/tasks/suggestions`, { withCredentials: true }),
          axios.get(`${API}/admin/settings`, { withCredentials: true }).catch(() => ({ data: { followup_overdue_days: 5 } })),
        ]);
        setStats(statsRes.data);
        setTodayTasks(tasksRes.data);
        setSuggestions(sugRes.data.slice(0, 3));
        setOverdueDays(settingsRes.data.followup_overdue_days || 5);
        // Run automation silently in background (fire-and-forget)
        axios.post(`${API}/automation/run`, {}, { withCredentials: true }).catch(() => {});
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  const saveThreshold = async (newDays) => {
    try {
      const v = Math.max(1, Math.min(90, parseInt(newDays, 10) || 5));
      await axios.put(`${API}/admin/settings`, { followup_overdue_days: v }, { withCredentials: true });
      setOverdueDays(v);
      setShowThresholdEdit(false);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleTask = async (task) => {
    try {
      await axios.post(`${API}/tasks/${task.id}/complete`, {}, { withCredentials: true });
      setTodayTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch { /* silent */ }
  };

  const loadDiagnostic = async () => {
    try {
      const res = await axios.get(`${API}/admin/diagnostic`, { withCredentials: true });
      setDiagnostic(res.data);
      setShowDiag(true);
    } catch (err) {
      alert("Failed to load diagnostic: " + (err.response?.data?.detail || err.message));
    }
  };

  const runImport = async () => {
    if (!window.confirm("This will import the seed data (forklifts, clients, suppliers, tasks). Existing items are not touched. Continue?")) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await axios.post(`${API}/admin/import-seed`, {}, { withCredentials: true });
      setImportResult(res.data);
      // Reload diagnostic after import
      const diag = await axios.get(`${API}/admin/diagnostic`, { withCredentials: true });
      setDiagnostic(diag.data);
      // Reload stats to reflect new counts
      const statsRes = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(statsRes.data);
    } catch (err) {
      alert("Import failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  const cards = stats ? [
    { icon: Truck, label: "Total Forklifts", value: stats.total_forklifts, sub: `${stats.available_forklifts} available`, to: "/admin/inventory" },
    { icon: Users, label: "Customer Requests", value: stats.total_requests, sub: `${stats.active_requests} active`, to: "/admin/requests" },
    { icon: Bell, label: "Notifications", value: stats.unread_notifications, sub: "Match alerts", to: "/admin/notifications" },
    { icon: Package, label: "Available Units", value: stats.available_forklifts, sub: "In inventory", to: "/admin/inventory" },
  ] : [];

  const todayStr = new Date().toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" });

  return (
    <div data-testid="admin-dashboard" className="p-8">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Overview of your forklift inventory and customer requests</p>
        </div>
        <button
          data-testid="diagnostic-btn"
          onClick={loadDiagnostic}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 px-3 py-1.5 rounded-sm hover:bg-gray-50"
        >
          <Database className="w-3.5 h-3.5" /> Diagnóstico / Restaurar datos
        </button>
      </div>

      {showDiag && diagnostic && (
        <div data-testid="diagnostic-panel" className="mb-8 bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
              <Database className="w-4 h-4 text-blue-500" /> Diagnóstico del sistema
            </h2>
            <button onClick={() => setShowDiag(false)} className="text-xs text-gray-400 hover:text-gray-600">Cerrar</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">Archivo seed</p>
              <div className={`p-3 rounded-sm ${diagnostic.seed_file.exists ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}>
                <p className="font-mono text-xs break-all">{diagnostic.seed_file.path}</p>
                <p className="text-xs mt-1">
                  {diagnostic.seed_file.exists ? `✓ OK (${Math.round(diagnostic.seed_file.size_bytes / 1024)} KB)` : "✗ NO ENCONTRADO"}
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">Base de datos</p>
              <div className="p-3 bg-gray-50 rounded-sm">
                <p className="text-xs"><strong>DB:</strong> {diagnostic.environment.db_name}</p>
                <p className="text-xs font-mono mt-1 break-all">{diagnostic.environment.mongo_url_preview}</p>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-2">Email (Resend)</p>
              <div className={`p-3 rounded-sm ${diagnostic.environment.resend_configured ? "bg-green-50 text-green-800" : "bg-yellow-50 text-yellow-800"}`}>
                <p className="text-xs">{diagnostic.environment.resend_configured ? "✓ Configurado" : "⚠ Falta API key"}</p>
                <p className="text-xs mt-1">De: {diagnostic.environment.sender_email}</p>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-xs font-semibold text-gray-500 mb-2">Conteo de documentos</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(diagnostic.db_counts).map(([col, count]) => (
                <div key={col} className={`p-2 border rounded-sm ${count === 0 ? "border-red-200 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                  <p className="text-xs text-gray-500">{col}</p>
                  <p className={`font-mono font-bold ${count === 0 ? "text-red-600" : "text-gray-900"}`}>{count}</p>
                </div>
              ))}
            </div>
          </div>

          {(diagnostic.db_counts.forklifts === 0 || diagnostic.db_counts.crm_clients === 0) && (
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-200 rounded-sm p-3 mb-4">
              <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-900">
                Tu base de datos parece vacía. Click en "Importar datos ahora" para restaurar los 34 montacargas + 5 clientes + 9 proveedores + 4 tareas desde el archivo seed.
              </p>
            </div>
          )}

          <button
            data-testid="import-seed-btn"
            onClick={runImport}
            disabled={importing || !diagnostic.seed_file.exists}
            className="bg-yellow-400 text-gray-900 font-bold px-4 py-2 rounded-sm hover:bg-yellow-500 disabled:opacity-50 text-sm inline-flex items-center gap-2"
          >
            {importing ? "Importando..." : "Importar datos ahora"}
          </button>

          {importResult && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-sm">
              <p className="text-xs font-semibold text-green-900 mb-2">✓ Import ejecutado</p>
              <div className="text-xs text-green-800 space-y-0.5 font-mono">
                {Object.entries(importResult.results).map(([col, r]) => (
                  <p key={col}>{col}: agregados={r.inserted}, ya existían={r.skipped_existing}, total en archivo={r.total_in_file}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Today's Agenda Widget */}
      <div className="mb-8 bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-yellow-500" />
              <h2 className="font-heading text-base font-bold text-gray-900">Today's Agenda</h2>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{todayStr}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Threshold selector */}
            <div className="relative">
              {!showThresholdEdit ? (
                <button
                  data-testid="threshold-display-btn"
                  onClick={() => setShowThresholdEdit(true)}
                  className="text-[11px] font-medium text-gray-500 hover:text-gray-900 border border-gray-200 px-2 py-1 rounded-sm hover:bg-gray-50"
                  title="Click to change overdue threshold"
                >
                  Overdue at: <span className="font-bold text-gray-900">{overdueDays}d</span>
                </button>
              ) : (
                <div className="flex items-center gap-1 border border-yellow-300 bg-yellow-50 rounded-sm px-1 py-0.5">
                  <input
                    type="number"
                    min="1"
                    max="90"
                    autoFocus
                    defaultValue={overdueDays}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveThreshold(e.target.value);
                      if (e.key === "Escape") setShowThresholdEdit(false);
                    }}
                    onBlur={(e) => saveThreshold(e.target.value)}
                    className="w-12 text-xs font-bold text-gray-900 bg-white border border-gray-200 rounded-sm px-1 py-0.5 text-center"
                    data-testid="threshold-input"
                  />
                  <span className="text-[10px] text-gray-500">days</span>
                </div>
              )}
            </div>
            {todayTasks.length > 0 && (() => {
              const overdueCount = todayTasks.filter((t) => (t.due_date || "").slice(0, 10) < new Date().toISOString().slice(0, 10)).length;
              return (
                <>
                  <span className="text-xs font-semibold bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">{todayTasks.length} pending</span>
                  {overdueCount > 0 && (
                    <span className="text-xs font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full" data-testid="agenda-overdue-badge">
                      {overdueCount} overdue
                    </span>
                  )}
                </>
              );
            })()}
            <button
              data-testid="agenda-add-task-btn"
              onClick={() => navigate("/admin/tasks")}
              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900 border border-gray-200 px-2 py-1 rounded-sm hover:bg-gray-50"
            >
              <Plus className="w-3 h-3" /> Add
            </button>
            <button onClick={() => navigate("/admin/tasks")} className="text-xs text-yellow-600 hover:text-yellow-800 font-medium inline-flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </div>

        {todayTasks.length === 0 && suggestions.length === 0 ? (
          <div className="px-5 py-6 text-center">
            <CheckSquare className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-700">All clear for today!</p>
            <button onClick={() => navigate("/admin/tasks")} className="text-xs text-yellow-600 hover:underline mt-1">Plan tomorrow's tasks</button>
          </div>
        ) : (
          <div>
            {(() => {
              const todayYmd = new Date().toISOString().slice(0, 10);
              const todayList = todayTasks.filter((t) => (t.due_date || "").slice(0, 10) === todayYmd);
              const overdueList = todayTasks.filter((t) => (t.due_date || "").slice(0, 10) < todayYmd);
              // Show ALL today's tasks always; cap overdue at 3 to avoid clutter
              const overdueShown = overdueList.slice(0, 3);
              const overdueHidden = Math.max(0, overdueList.length - overdueShown.length);
              const visible = [...todayList, ...overdueShown];
              return (
                <>
                  {visible.map((task) => {
              const Icon = TYPE_ICON[task.task_type] || CircleDot;
              const dueYmd = (task.due_date || "").slice(0, 10);
              let dateLabel = "—";
              let dateClass = "bg-gray-100 text-gray-600";
              let isOverdue = false;
              if (dueYmd) {
                if (dueYmd === todayYmd) {
                  dateLabel = "Today";
                  dateClass = "bg-yellow-100 text-yellow-800";
                } else if (dueYmd < todayYmd) {
                  const diff = Math.round((new Date(todayYmd) - new Date(dueYmd)) / 86400000);
                  dateLabel = `Overdue ${diff}d`;
                  dateClass = "bg-red-100 text-red-700";
                  isOverdue = true;
                } else {
                  // Future task (shouldn't appear with today_overdue but defensive)
                  const d = new Date(dueYmd);
                  dateLabel = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                  dateClass = "bg-blue-100 text-blue-700";
                }
              }
              const goToClient = (e) => {
                if (!task.client_id) return;
                e.stopPropagation();
                navigate(`/admin/crm/clients/${task.client_id}`);
              };
              // Days since last contact + color coding
              const dsc = task.days_since_contact;
              const isFollowupOverdue = task.task_type === "follow_up" && typeof dsc === "number" && dsc > overdueDays;
              let daysClass = "bg-gray-100 text-gray-600";
              let daysLabel = "";
              if (typeof dsc === "number") {
                if (dsc > overdueDays) {
                  daysClass = "bg-red-100 text-red-700 font-bold";
                  daysLabel = `${dsc}d · OVERDUE`;
                } else if (dsc > overdueDays / 2) {
                  daysClass = "bg-yellow-100 text-yellow-800";
                  daysLabel = `${dsc}d`;
                } else {
                  daysClass = "bg-green-100 text-green-700";
                  daysLabel = `${dsc}d`;
                }
              }
              return (
                <div
                  key={task.id}
                  data-testid={`agenda-task-${task.id}`}
                  onClick={goToClient}
                  role={task.client_id ? "button" : undefined}
                  className={`flex items-start gap-3 px-5 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${task.client_id ? "cursor-pointer" : ""} ${isOverdue || isFollowupOverdue ? "bg-red-50/40" : ""}`}
                >
                  <button onClick={(e) => { e.stopPropagation(); toggleTask(task); }} className="flex-shrink-0 mt-0.5" title="Mark as done">
                    <Square className="w-4 h-4 text-gray-300 hover:text-green-500" />
                  </button>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${PRIORITY_DOT[task.priority] || PRIORITY_DOT.medium}`} />
                  <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 truncate">{task.title}</p>
                    {task.client_name && (
                      <p className="text-xs text-blue-600 hover:underline truncate" data-testid={`agenda-task-client-${task.id}`}>
                        → {task.client_name}
                      </p>
                    )}
                    {task.client_id && (
                      <p className="text-xs text-gray-700 mt-1 italic truncate" data-testid={`agenda-task-last-note-${task.id}`}>
                        Last: {task.last_interaction_note ? (
                          <span className="font-bold not-italic">"{task.last_interaction_note}"</span>
                        ) : (
                          <span className="text-gray-400 not-italic">No notes yet</span>
                        )}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${dateClass}`} data-testid={`agenda-task-date-${task.id}`}>
                      {dateLabel}
                    </span>
                    {daysLabel && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${daysClass}`} data-testid={`agenda-task-days-${task.id}`}>
                        {daysLabel}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
                  {overdueHidden > 0 && (
                    <button onClick={() => navigate("/admin/tasks")} className="w-full text-xs text-red-500 hover:text-red-700 py-2 border-b border-gray-50 font-medium">
                      +{overdueHidden} more overdue tasks — view all
                    </button>
                  )}
                </>
              );
            })()}

            {/* Suggestions strip */}
            {suggestions.length > 0 && (
              <div className="px-5 py-3 bg-blue-50 border-t border-blue-100">
                <div className="flex items-center gap-1.5 mb-2">
                  <Lightbulb className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-semibold text-blue-700">Suggested follow-ups</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button
                      key={s.client_id}
                      data-testid={`agenda-suggestion-${s.client_id}`}
                      onClick={() => navigate(`/admin/tasks?client_id=${s.client_id}&client_name=${encodeURIComponent(s.client_name)}`)}
                      className="text-xs text-blue-700 bg-white border border-blue-200 px-2 py-1 rounded-sm hover:bg-blue-50"
                    >
                      {s.client_name} <span className="text-blue-400">({s.days_since_contact ?? "?"}d)</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((card, i) => (
            <div key={i} data-testid={`stat-card-${i}`} onClick={() => navigate(card.to)}
              className="bg-white border border-gray-200 rounded-sm p-5 cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-500">{card.label}</p>
                <card.icon className="w-5 h-5 text-gray-400" />
              </div>
              <p className="font-heading text-3xl font-bold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500 mt-1">{card.sub}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
